import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Select, InputNumber, Upload, Space, Popconfirm,
  Input, message, Typography, Divider,
} from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title } = Typography;
const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: `Bearer ${token()}` });

export default function InventoryCalc() {
  // ========== 状态 ==========
  const [models, setModels] = useState([]);           // 型号选项
  const [types, setTypes] = useState([]);             // 价格属性选项
  const [data, setData] = useState([]);               // 列表数据
  const [loading, setLoading] = useState(false);

  // 库存天数计算结果
  const [calcResult, setCalcResult] = useState([]);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcRan, setCalcRan] = useState(false);   // 是否已执行过计算

  // 新增表单
  const [addModel, setAddModel] = useState(null);     // 选择的型号
  const [addQty, setAddQty] = useState(null);          // 数量
  const [avgIndex, setAvgIndex] = useState(null);      // 三个月平均指标
  const [calcType, setCalcType] = useState('');         // 类型（动态加载）

  // 编辑状态
  const [editingKey, setEditingKey] = useState(null);
  const [editModel, setEditModel] = useState('');
  const [editQty, setEditQty] = useState(null);

  // ========== 加载 ==========
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [modelsRes, listRes, typesRes] = await Promise.all([
        axios.get(`${API}/inventory/models`, { headers: h() }),
        axios.get(`${API}/inventory/list`, { headers: h() }),
        axios.get(`${API}/inventory/types`, { headers: h() }),
      ]);
      if (modelsRes.data.success) setModels(modelsRes.data.data || []);
      if (listRes.data.success) setData(listRes.data.data || []);
      if (typesRes.data.success) setTypes(typesRes.data.data || []);
    } catch (e) {
      message.error('加载数据失败');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // 页面加载只取型号和类型下拉选项，不读 cus_kc 库存列表
    (async () => {
      try {
        const [modelsRes, typesRes] = await Promise.all([
          axios.get(`${API}/inventory/models`, { headers: h() }),
          axios.get(`${API}/inventory/types`, { headers: h() }),
        ]);
        if (modelsRes.data.success) setModels(modelsRes.data.data || []);
        if (typesRes.data.success) setTypes(typesRes.data.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);
  
  // types 加载后设置默认值
  useEffect(() => {
    if (types.length > 0 && !calcType) {
      setCalcType(types[0]);
    }
  }, [types]);

  // ========== Excel 导入 ==========
  const handleImport = async (file) => {
    // 算完后导入，清空列表再显示新数据
    if (calcRan) { setData([]); setCalcRan(false); }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/inventory/import`, fd, { headers: { ...h(), 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        const now = new Date().toISOString();
        setData((res.data.data || []).map(d => ({ ...d, ddate: now })));
        message.success(res.data.message || '导入成功');
      } else {
        message.error(res.data.message || '导入失败');
      }
    } catch (e) {
      message.error('导入失败: ' + (e.response?.data?.detail || e.message));
    }
    return false;
  };

  // ========== 新增 ==========
  const handleAdd = () => {
    if (!addModel) { message.warning('请选择型号'); return; }
    if (addQty == null || addQty === '') { message.warning('请输入数量'); return; }

    // 算完后手工添加，清空列表
    const base = calcRan ? [] : data;
    if (calcRan) setCalcRan(false);

    const exists = base.find(d => d.cinvstd === addModel);
    const item = { cinvstd: addModel, inum: Number(addQty) };
    if (exists) { item.ident = exists.ident; }

    const newData = exists
      ? base.map(d => d.cinvstd === addModel ? { ...d, inum: Number(addQty), _dirty: true } : d)
      : [...base, { ident: null, ccuscode: '', cinvstd: addModel, inum: Number(addQty), ddate: new Date().toISOString(), _new: true }];

    setData(newData);
    setAddModel(null);
    setAddQty(null);
  };

  // ========== 编辑 ==========
  const startEdit = (record) => {
    setEditingKey(record.ident || record.cinvstd);
    setEditModel(record.cinvstd);
    setEditQty(record.inum);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditModel('');
    setEditQty(null);
  };

  const saveEdit = () => {
    if (!editModel) { message.warning('型号不能为空'); return; }
    setData(prev => prev.map(d =>
      (d.ident || d.cinvstd) === editingKey
        ? { ...d, cinvstd: editModel, inum: Number(editQty), _dirty: true }
        : d
    ));
    cancelEdit();
  };

  // ========== 删除 ==========
  const handleDelete = (record) => {
    setData(prev => prev.map(d =>
      (d.ident || d.cinvstd) === (record.ident || record.cinvstd)
        ? { ...d, _deleted: true }
        : d
    ).filter(d => !d._deleted));
  };

  // ========== 保存到服务器 ==========
  const handleSave = async () => {
    const items = data
      .filter(d => !d._deleted)
      .map(d => ({ ident: d.ident || undefined, cinvstd: d.cinvstd, inum: d.inum }));

    setLoading(true);
    try {
      const res = await axios.post(`${API}/inventory/save`, {
        items,
        avg_index: avgIndex,
        calc_type: calcType,
      }, { headers: h() });
      if (res.data.success) {
        message.success('保存成功');
        fetchData();
      } else {
        message.error(res.data.message || '保存失败');
      }
    } catch (e) {
      message.error('保存失败');
    }
    setLoading(false);
  };

  // ========== 库存天数计算 ==========
  const handleCalc = async () => {
    setCalcLoading(true);
    try {
      const [calcRes, listRes] = await Promise.all([
        axios.get(`${API}/inventory/calc-days`, { headers: h() }),
        axios.get(`${API}/inventory/list`, { headers: h() }),
      ]);
      if (calcRes.data.success) {
        setCalcResult(calcRes.data.data || []);
      }
      if (listRes.data.success) {
        setData(listRes.data.data || []);
      }
      setCalcRan(true);
      message.success('计算完成');
    } catch (e) {
      message.error('计算失败');
    }
    setCalcLoading(false);
  };

  // ========== 表格列 ==========
  const columns = [
    {
      title: '序号', width: 60,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: '规格型号', dataIndex: 'cinvstd', width: 180,
      render: (text, record) => {
        const key = record.ident || record.cinvstd;
        if (editingKey === key) {
          return (
            <Select
              showSearch
              value={editModel}
              onChange={setEditModel}
              style={{ width: '100%' }}
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            >
              {models.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
            </Select>
          );
        }
        return text;
      },
    },
    {
      title: '库存数量（盒）', dataIndex: 'inum', width: 140,
      render: (text, record) => {
        const key = record.ident || record.cinvstd;
        if (editingKey === key) {
          return <InputNumber value={editQty} onChange={setEditQty} min={0} step={0.01} style={{ width: '100%' }} />;
        }
        return text;
      },
    },
    {
      title: '导入时间', dataIndex: 'ddate', width: 180,
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作', width: 120,
      render: (_, record) => {
        const key = record.ident || record.cinvstd;
        if (editingKey === key) {
          return (
            <Space>
              <Button type="link" size="small" icon={<SaveOutlined />} onClick={saveEdit}>保存</Button>
              <Button type="link" size="small" icon={<CloseOutlined />} onClick={cancelEdit}>取消</Button>
            </Space>
          );
        }
        return (
          <Space>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)}>编辑</Button>
            <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>天数核算</Title>

      {/* 数据导入区域 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleImport}>
            <Button icon={<UploadOutlined />}>Excel 导入</Button>
          </Upload>

          <Divider type="vertical" />

          <span style={{ color: '#666' }}>型号：</span>
          <Select
            showSearch
            value={addModel}
            onChange={setAddModel}
            placeholder="选择型号"
            style={{ width: 180 }}
            filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          >
            {models.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
          </Select>

          <span style={{ color: '#666' }}>数量（单位：盒）：</span>
          <InputNumber value={addQty} onChange={setAddQty} placeholder="库存数量" min={0} step={0.01} style={{ width: 140 }} />

          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加</Button>
        </Space>

        <Divider style={{ margin: '12px 0' }} />

        <Space>
          <span style={{ color: '#666', fontWeight: 500 }}>三个月平均指标（爱渠道）：</span>
          <InputNumber value={avgIndex} onChange={setAvgIndex} placeholder="输入指标" min={0} step={0.01} style={{ width: 180 }} />
        </Space>

        <Divider type="vertical" />

        <Space>
          <span style={{ color: '#666', fontWeight: 500 }}>类型：</span>
          <Select value={calcType} onChange={setCalcType} style={{ width: 160 }} placeholder="选择价格属性">
            {types.map(t => <Select.Option key={t} value={t}>{t}</Select.Option>)}
          </Select>

          <Button type="primary" icon={<SaveOutlined />} onClick={handleCalc} loading={calcLoading}>库存天数计算</Button>
        </Space>
      </Card>

      {/* 计算结果 */}
      {calcResult.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }} title="库存天数计算结果">
          <Table
            rowKey="ccuscode"
            dataSource={calcResult}
            pagination={false}
            size="small"
            columns={[
              { title: '客户代码', dataIndex: 'ccuscode', width: 120 },
              { title: '库存金额', dataIndex: 'stock_amount', width: 140, render: v => v?.toLocaleString() },
              { title: '当前库存天数', dataIndex: 'stock_days', width: 130, render: v => v?.toFixed(2) },
              { title: '需采购金额（含税）', dataIndex: 'need_purchase', width: 160, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
              { title: '月平均指标', dataIndex: 'avg_index', width: 140, render: v => v?.toLocaleString() },
              { title: '类型', dataIndex: 'calc_type', width: 140 },
            ]}
          />
        </Card>
      )}

      {/* 数据列表 */}
      <Card
        size="small"
        title={`当前库存数据（${data.length} 条）`}
        extra={
          <Space size="middle">
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={loading}>保存</Button>
            <Popconfirm title="确认清空列表数据？" onConfirm={() => setData([])}>
              <Button icon={<DeleteOutlined />} danger>清空数据</Button>
            </Popconfirm>
          </Space>
        }
      >
        <Table
          rowKey={r => r.ident || r.cinvstd}
          columns={columns}
          dataSource={data}
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: '暂无数据，请导入 Excel 或手动添加' }}
        />
      </Card>
    </div>
  );
}
